import { useState } from 'react';
import { Download, Loader2, Database, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { apiFetch } from '@/lib/api';
import { useOrganizationList } from '@clerk/clerk-react';

async function downloadJson(url: string, filename: string) {
  const response = await apiFetch<unknown>(url);
  const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function PrivacyTab() {
  const [exportingProfile, setExportingProfile] = useState(false);
  const [exportingBackup, setExportingBackup] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [backupError, setBackupError] = useState<string | null>(null);

  const { userMemberships } = useOrganizationList({ userMemberships: true });
  const isAdmin = userMemberships?.data?.some((m) => m.role === 'org:admin') ?? false;

  const handleExportProfile = async () => {
    setExportingProfile(true);
    setProfileError(null);
    try {
      const timestamp = new Date().toISOString().split('T')[0];
      await downloadJson('/profiles/me/export', `eligaro-profile-${timestamp}.json`);
    } catch (error) {
      setProfileError(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setExportingProfile(false);
    }
  };

  const handleExportBackup = async () => {
    setExportingBackup(true);
    setBackupError(null);
    try {
      const timestamp = new Date().toISOString().split('T')[0];
      await downloadJson('/backup/export', `eligaro-tenant-backup-${timestamp}.json`);
    } catch (error) {
      setBackupError(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setExportingBackup(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-1 text-sm font-semibold">Privacy & Data</h3>
        <p className="text-sm text-muted-foreground">
          Download your data or export a full tenant backup.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <User className="h-4 w-4" />
            My Profile Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-sm text-muted-foreground">
            Download a complete export of your profile, skills, work experience, and saved
            assignments as a JSON file.
          </p>
          {profileError && <p className="mb-3 text-sm text-destructive">{profileError}</p>}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportProfile}
            disabled={exportingProfile}
          >
            {exportingProfile ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="mr-1.5 h-3.5 w-3.5" />
            )}
            Export My Data
          </Button>
        </CardContent>
      </Card>

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Database className="h-4 w-4" />
              Tenant Backup
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-sm text-muted-foreground">
              Export a full backup of all tenant data including profiles, assignments, and matches.
              Available to organization admins only.
            </p>
            {backupError && <p className="mb-3 text-sm text-destructive">{backupError}</p>}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportBackup}
              disabled={exportingBackup}
            >
              {exportingBackup ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="mr-1.5 h-3.5 w-3.5" />
              )}
              Export Tenant Backup
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
